// Wave 20.49cu — Gesture action BAR for AI video surfaces.
//
// Replaces GestureActionTray (corner circles). Rider's verdict on the
// tray: tiny, overlapping the top-right chrome, and non-functional.
// Two real bugs sat underneath "non-functional":
//   1. Callers passed `hands={latestHandsRef.current}` — a snapshot at
//      render time. The hand detector writes the ref at 5 Hz without
//      re-rendering, so the tray only ever saw stale hands. The bar
//      takes the REF and reads `.current` inside its own rAF loop.
//   2. Landmarks were mapped onto the container rect, but Preflight's
//      video is object-fit: cover inside a 3:4 box with a 16:9 camera
//      — the sides are cropped, so the mapping was wrong by up to a
//      third of the width. `mapLandmark` now goes through the video's
//      rendered crop (videoRef), falling back to the container.
//
// Layout: a full-width strip pinned to the top of the video box. It
// OWNS that strip — surfaces move their top chrome (Live pill,
// fullscreen, calibration chips) into the `trailing` slot or below
// GESTURE_BAR_HEIGHT, so nothing can overlap the buttons again.
//
// Buttons are big pills (≥ 60 px tall, icon + label). Interaction:
//   • hand near a pill (rect + 40 px) → ARMED: pill grows in place,
//     a progress fill runs along it, a hint reads "hold · or 👍";
//   • keep it there `dwellMs` (900) → fires; or
//   • thumbs-up anywhere in frame, held 250 ms → fires the armed
//     action (or the only/first enabled one — no aiming needed);
//   • click / tap always works.
// A "HAND" indicator on the right lights up whenever a hand has been
// seen in the last 700 ms, so the rider can tell tracking is alive.

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../theme';
import type { DetectedHand } from '../vision/handDetector';
import { isThumbsUp } from '../vision/handShape';

export type GestureTone = 'primary' | 'danger' | 'muted';

export interface GestureAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  tone?: GestureTone;
  onFire: () => void;
  disabled?: boolean;
}

export interface GestureActionBarProps {
  actions: GestureAction[];
  /** The detector's live ref — read every frame, never snapshotted. */
  handsRef: React.MutableRefObject<DetectedHand[] | null | undefined>;
  /** The <video> the landmarks are normalized to (for cover-crop math). */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  /** Video is CSS-mirrored so hand x needs flipping. Preflight true. */
  mirrorX?: boolean;
  dwellMs?: number;
  thumbsUpHoldMs?: number;
  onArm?: (id: string) => void;
  /** Chrome that lives in the bar's right slot (Live pill, fullscreen…). */
  trailing?: React.ReactNode;
  leading?: React.ReactNode;
}

/** Surfaces offset their own top overlays by this so they sit below
 *  the bar. Matches the bar's rendered height (xs / md+). */
export const GESTURE_BAR_HEIGHT = { xs: 72, md: 84 } as const;

const DEFAULT_DWELL_MS = 900;
const DEFAULT_THUMBS_UP_HOLD_MS = 250;
const PROXIMITY_PAD_PX = 40;
const HAND_SEEN_MS = 700;

function toneColor(tone: GestureTone | undefined): string {
  if (tone === 'danger') return colors.danger;
  if (tone === 'muted') return colors.textMuted;
  return colors.brand;
}

export default function GestureActionBar({
  actions,
  handsRef,
  videoRef,
  mirrorX = false,
  dwellMs = DEFAULT_DWELL_MS,
  thumbsUpHoldMs = DEFAULT_THUMBS_UP_HOLD_MS,
  onArm,
  trailing,
  leading,
}: GestureActionBarProps) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const actionsRef = useRef(actions);
  const mirrorXRef = useRef(mirrorX);
  const dwellMsRef = useRef(dwellMs);
  const holdMsRef = useRef(thumbsUpHoldMs);
  const onArmRef = useRef(onArm);
  useEffect(() => { actionsRef.current = actions; }, [actions]);
  useEffect(() => { mirrorXRef.current = mirrorX; }, [mirrorX]);
  useEffect(() => { dwellMsRef.current = dwellMs; }, [dwellMs]);
  useEffect(() => { holdMsRef.current = thumbsUpHoldMs; }, [thumbsUpHoldMs]);
  useEffect(() => { onArmRef.current = onArm; }, [onArm]);

  const [armedId, setArmedId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [handSeen, setHandSeen] = useState(false);
  const armedIdRef = useRef<string | null>(null);
  const armStartRef = useRef<number | null>(null);
  const thumbsStartRef = useRef<number | null>(null);
  const lastHandMsRef = useRef(0);
  const handSeenRef = useRef(false);

  const fireAction = useCallback((id: string) => {
    const a = actionsRef.current.find((x) => x.id === id);
    if (!a || a.disabled) return;
    armedIdRef.current = null;
    armStartRef.current = null;
    thumbsStartRef.current = null;
    setArmedId(null);
    setProgress(0);
    try { a.onFire(); } catch { /* caller-owned error surface */ }
  }, []);

  // Reset armed state when the action set changes (e.g. state machine
  // swaps Start Calibration → Start Session).
  useEffect(() => {
    armedIdRef.current = null;
    armStartRef.current = null;
    setArmedId(null);
    setProgress(0);
  }, [actions]);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const parentEl = barRef.current?.parentElement;
      if (!parentEl) return;
      const parentRect = parentEl.getBoundingClientRect();
      const now = performance.now();
      const hands = handsRef.current ?? [];

      // Landmark (normalized to the VIDEO frame) → viewport px, through
      // the video's rendered crop when it's object-fit: cover.
      const vid = videoRef?.current;
      let renderW = parentRect.width;
      let renderH = parentRect.height;
      let offX = 0;
      let offY = 0;
      if (vid && vid.videoWidth > 0 && vid.videoHeight > 0) {
        const va = vid.videoWidth / vid.videoHeight;
        const ca = parentRect.width / parentRect.height;
        if (va > ca) { renderH = parentRect.height; renderW = renderH * va; offX = (parentRect.width - renderW) / 2; }
        else { renderW = parentRect.width; renderH = renderW / va; offY = (parentRect.height - renderH) / 2; }
      }
      const mapLm = (lm: { x: number; y: number }) => ({
        x: parentRect.left + offX + (mirrorXRef.current ? 1 - lm.x : lm.x) * renderW,
        y: parentRect.top + offY + lm.y * renderH,
      });

      // Hand-seen indicator.
      if (hands.length > 0) lastHandMsRef.current = now;
      const seen = now - lastHandMsRef.current < HAND_SEEN_MS;
      if (seen !== handSeenRef.current) { handSeenRef.current = seen; setHandSeen(seen); }

      // Proximity hit test — first pill any landmark comes near.
      let hitId: string | null = null;
      if (hands.length > 0) {
        outer: for (const a of actionsRef.current) {
          if (a.disabled) continue;
          const btn = btnRefs.current.get(a.id);
          if (!btn) continue;
          const r = btn.getBoundingClientRect();
          const l = r.left - PROXIMITY_PAD_PX, t = r.top - PROXIMITY_PAD_PX;
          const rr = r.right + PROXIMITY_PAD_PX, b = r.bottom + PROXIMITY_PAD_PX;
          for (const hand of hands) {
            for (const lm of hand.landmarks) {
              const p = mapLm(lm);
              if (p.x >= l && p.x <= rr && p.y >= t && p.y <= b) { hitId = a.id; break outer; }
            }
          }
        }
      }

      if (hitId !== armedIdRef.current) {
        armedIdRef.current = hitId;
        armStartRef.current = hitId ? now : null;
        setArmedId(hitId);
        setProgress(0);
        if (hitId) onArmRef.current?.(hitId);
      }

      // Thumbs-up fire path (held).
      const anyThumbs = hands.some((h) => isThumbsUp(h));
      if (anyThumbs) {
        if (thumbsStartRef.current == null) thumbsStartRef.current = now;
        else if (now - thumbsStartRef.current >= holdMsRef.current) {
          const target = armedIdRef.current ?? actionsRef.current.find((a) => !a.disabled)?.id ?? null;
          if (target) { fireAction(target); return; }
          thumbsStartRef.current = null;
        }
      } else {
        thumbsStartRef.current = null;
      }

      // Hover-dwell fire path.
      if (armedIdRef.current && armStartRef.current != null) {
        const p = Math.min(1, (now - armStartRef.current) / Math.max(1, dwellMsRef.current));
        setProgress(p);
        if (p >= 1) fireAction(armedIdRef.current);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [fireAction, handsRef, videoRef]);

  if (actions.length === 0 && !trailing && !leading) return null;

  return (
    <Box
      ref={barRef}
      role="toolbar"
      aria-label="Session actions"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: GESTURE_BAR_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: { xs: 1.25, md: 2 },
        background: 'linear-gradient(180deg, rgba(10,14,26,0.94) 0%, rgba(10,14,26,0.72) 75%, rgba(10,14,26,0) 100%)',
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      {leading ? <Box sx={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center' }}>{leading}</Box> : null}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, flex: 1, minWidth: 0 }}>
        {actions.map((a) => {
          const isArmed = armedId === a.id;
          const p = isArmed ? progress : 0;
          const color = toneColor(a.tone);
          return (
            <Box
              key={a.id}
              component="button"
              type="button"
              ref={(el: HTMLButtonElement | null) => {
                if (el) btnRefs.current.set(a.id, el);
                else btnRefs.current.delete(a.id);
              }}
              onClick={() => fireAction(a.id)}
              disabled={a.disabled}
              aria-label={a.label}
              sx={{
                pointerEvents: 'auto',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minHeight: { xs: 52, md: 60 },
                px: { xs: 2, md: 2.75 },
                borderRadius: 999,
                border: `2px solid ${color}${isArmed ? 'FF' : 'AA'}`,
                background: `linear-gradient(90deg, ${color}55 ${p * 100}%, rgba(10,14,26,0.88) ${p * 100}%)`,
                color: colors.text,
                cursor: a.disabled ? 'default' : 'pointer',
                opacity: a.disabled ? 0.4 : 1,
                transform: isArmed ? 'scale(1.08)' : 'scale(1)',
                transformOrigin: 'center',
                boxShadow: isArmed ? `0 0 28px ${color}77` : '0 2px 10px rgba(0,0,0,0.5)',
                transition: 'transform 160ms cubic-bezier(.2,.7,.3,1.2), box-shadow 160ms ease, border-color 120ms ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', color, fontSize: { xs: 24, md: 28 }, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}>
                {a.icon}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.05 }}>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: { xs: 12, md: 13 }, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#fff' }}>
                  {a.label}
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: isArmed ? color : colors.textMuted, mt: 0.25 }}>
                  {isArmed ? 'HOLD · OR 👍' : 'HOVER · 👍 · TAP'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Hand-tracking indicator */}
      <Box
        aria-live="polite"
        sx={{
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 999,
          border: `1px solid ${handSeen ? colors.brand : colors.border}`,
          backgroundColor: 'rgba(10,14,26,0.7)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: handSeen ? colors.brand : colors.textMuted, boxShadow: handSeen ? `0 0 10px ${colors.brand}` : 'none', transition: 'background-color 120ms ease' }} />
        <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', color: handSeen ? colors.text : colors.textMuted }}>
          HAND
        </Typography>
      </Box>

      {trailing ? <Box sx={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{trailing}</Box> : null}
    </Box>
  );
}
